import { ROUTES } from '@/models/router.model';
import { useAppState } from '@/store';
import { selectAdminProfile } from '@/modules/AdminProfile';
import { Resource } from '../constants';
import { usePermissions } from './usePermissions';

// Maps a pathname to the resource(s) that guard it. Ordered most-specific first
// so that prefix matches do not shadow nested routes.
const ROUTE_RESOURCES: Array<{ test: (path: string) => boolean; resources: Resource[] }> = [
  { test: (p) => p === ROUTES.DASHBOARD, resources: [Resource.DASHBOARD] },
  { test: (p) => p.startsWith(ROUTES.CANCELLED_ORDERS), resources: [Resource.DASHBOARD] },
  { test: (p) => p.startsWith(ROUTES.PROFILE), resources: [Resource.PROFILE] },
  { test: (p) => p.startsWith(ROUTES.REGIONS_AND_PICKUP_INDEX), resources: [Resource.REGION, Resource.PICKUP] },
  { test: (p) => p.startsWith(ROUTES.PICKUP), resources: [Resource.PICKUP] },
  { test: (p) => p.startsWith(ROUTES.REGION), resources: [Resource.REGION] },
  { test: (p) => p.startsWith(ROUTES.CATEGORY_FILTERS), resources: [Resource.FILTER] },
  {
    test: (p) => p.startsWith(ROUTES.CATEGORIES),
    resources: [Resource.CATEGORY, Resource.FILTER, Resource.POPULAR_CATEGORY],
  },
  { test: (p) => p.startsWith(ROUTES.IMPORT_STOCKS), resources: [Resource.IMPORT_PRICE] },
  { test: (p) => p.startsWith(ROUTES.IMPORTS_LIST), resources: [Resource.IMPORT_LIST] },
  { test: (p) => p.startsWith(ROUTES.IMPORT), resources: [Resource.IMPORT_PRODUCT] },
  { test: (p) => p.startsWith(ROUTES.GENERATE_QR), resources: [Resource.QR_GENERATION] },
  {
    test: (p) => p.startsWith(ROUTES.PRODUCTS),
    resources: [Resource.PRODUCT, Resource.PRODUCT_OF_MONTH, Resource.TOP_PRODUCT],
  },
  // Stock balances have a dedicated resource (matches the menu gate).
  { test: (p) => p.startsWith(ROUTES.STOCK_BALANCES), resources: [Resource.STOCK_BALANCE] },
  { test: (p) => p.startsWith(ROUTES.ORDERS), resources: [Resource.ORDER] },
  { test: (p) => p.startsWith(ROUTES.USERS_LIST_INDEX), resources: [Resource.ADMIN_PANEL, Resource.SITE_USER] },
  { test: (p) => p.startsWith(ROUTES.PARTNERS_LIST), resources: [Resource.PARTNER] },
  { test: (p) => p.startsWith(ROUTES.PARTNER_DETAILS_INDEX), resources: [Resource.PARTNER] },
  { test: (p) => p.startsWith(ROUTES.BANNERS), resources: [Resource.BANNER] },
  { test: (p) => p.startsWith(ROUTES.SUBSCRIPTIONS), resources: [Resource.SUBSCRIPTION] },
  { test: (p) => p.startsWith(ROUTES.CONTENT_PAGES), resources: [Resource.CONTENT] },
  { test: (p) => p.startsWith(ROUTES.SETTINGS), resources: [Resource.SETTING] },
  { test: (p) => p.startsWith(ROUTES.SEO), resources: [Resource.SEO] },
  { test: (p) => p.startsWith(ROUTES.FEEDS), resources: [Resource.FEED] },
  { test: (p) => p.startsWith(ROUTES.COMMISSION), resources: [Resource.COMMISSION] },
  { test: (p) => p.startsWith(ROUTES.ACCOUNTING_CASH_REGISTERS_INDEX), resources: [Resource.CASH_REGISTER] },
  { test: (p) => p.startsWith(ROUTES.FEEDBACKS), resources: [Resource.FEEDBACK] },
];

// Routes that require MANAGE (not just view), checked before the view map.
const MANAGE_ROUTES: Array<{ test: (path: string) => boolean; resource: Resource }> = [
  { test: (p) => p.startsWith(ROUTES.PRODUCT_CREATE), resource: Resource.PRODUCT },
  // Content-page editor (create / details) — viewing the list needs content.view,
  // but opening the editor requires content.manage.
  { test: (p) => p.startsWith(`${ROUTES.CONTENT_PAGES}/`), resource: Resource.CONTENT },
  // Admin create/edit form (/users-list/admins/<id|create>) is a write screen — requires
  // admin_panel.manage; the list (/users-list, /users-list/admins) stays on admin_panel.view.
  { test: (p) => p.startsWith(`${ROUTES.USERS_LIST_INDEX}/admins/`), resource: Resource.ADMIN_PANEL },
];

/**
 * Whether the current admin may open a route. Unknown/utility routes are allowed;
 * page-level write restrictions are handled inside the pages.
 */
export const useRoutePermissions = (location: ROUTES): boolean => {
  const profile = useAppState(selectAdminProfile);
  const { canView, canManage } = usePermissions();
  const path = location as string;

  // While the profile (and its permissions) is still loading, do not redirect —
  // otherwise a deep link / page refresh would bounce to /profile before the
  // permissions arrive.
  if (!profile) return true;

  // Profile is the universal fallback landing — never gate it (avoids a blank
  // redirect-to-self when an account ends up without any permissions).
  if (path.startsWith(ROUTES.PROFILE)) return true;

  const manageMatch = MANAGE_ROUTES.find((entry) => entry.test(path));

  if (manageMatch) return canManage(manageMatch.resource);

  const match = ROUTE_RESOURCES.find((entry) => entry.test(path));

  if (!match) return true;

  return match.resources.some(canView);
};
